import React, { useRef, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, View } from 'react-native';
import * as MediaLibrary from 'expo-media-library';
import * as Sharing from 'expo-sharing';
import { captureRef } from 'react-native-view-shot';
import QRCode from 'react-native-qrcode-svg';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useMyPublicProfile } from '@/hooks/use-public-profile';
import { useToastStore } from '@/store/toastStore';

const PUBLIC_BASE_URL = 'https://controlapag.com.br';
const DEFAULT_BRAND_COLOR = '#FD5001';

export function QrCodeScreen() {
  const { addToast } = useToastStore();
  const { data: profile, isLoading } = useMyPublicProfile();

  const cardRef = useRef<View>(null);
  const qrRef = useRef<{ toDataURL: (cb: (data: string) => void) => void } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  if (isLoading || !profile) {
    return (
      <View className="flex-1 items-center justify-center">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // O profissional de equipe não tem vitrine própria: o QR leva para a página
  // dele dentro da organização.
  const org = profile.organization ?? null;
  const orgPath = org?.slug ? `@${org.slug}/professional/${org.providerId}` : null;
  const ownPath = profile.slug && profile.isPublic ? `@${profile.slug}` : null;
  const path = ownPath ?? orgPath;

  if (!path) {
    return (
      <View className="flex-1 items-center justify-center gap-3 p-8">
        <Ionicons className="text-neutral-400" name="qr-code-outline" size={48} />
        <ThemedText className="text-center text-xl font-bold">
          Publique seu perfil primeiro
        </ThemedText>
        <ThemedText className="text-center text-foreground/60">
          {org
            ? `O QR code leva para a sua página dentro de ${org.name}. Quando o dono publicar a página, ele aparece aqui.`
            : 'O QR code leva para a sua página pública. Ative o perfil público para gerá-lo.'}
        </ThemedText>
        <Button title="Voltar" onPress={() => router.back()} />
      </View>
    );
  }

  const brandColor = profile.brandColor ?? DEFAULT_BRAND_COLOR;
  const publicUrl = `${PUBLIC_BASE_URL}/${path}`;
  const qrValue = `${publicUrl}?utm_source=qr_code`;
  const displayUrl = `${PUBLIC_BASE_URL.replace('https://', '')}/${path}`;

  const captureCard = () =>
    captureRef(cardRef, { format: 'png', quality: 1, result: 'tmpfile' });

  const handleSaveToGallery = async () => {
    setIsSaving(true);
    try {
      if (Platform.OS === 'web') {
        // Web: baixa o PNG do QR gerado pelo SVG
        qrRef.current?.toDataURL((data) => {
          const link = document.createElement('a');
          link.href = `data:image/png;base64,${data}`;
          link.download = `qr-code-${profile.slug ?? org?.slug ?? 'perfil'}.png`;
          link.click();
        });
        return;
      }

      const { status } = await MediaLibrary.requestPermissionsAsync(true);
      if (status !== 'granted') {
        addToast('Permissão para salvar nas fotos é necessária.', 'error');
        return;
      }

      const uri = await captureCard();
      await MediaLibrary.saveToLibraryAsync(uri);
      addToast('QR code salvo na galeria!', 'success');
    } catch {
      addToast('Erro ao salvar o QR code.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleShare = async () => {
    setIsSharing(true);
    try {
      if (Platform.OS === 'web') {
        if (typeof navigator !== 'undefined' && navigator.share) {
          await navigator.share({ url: qrValue });
        } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
          await navigator.clipboard.writeText(qrValue);
          addToast('Link copiado!', 'success');
        }
        return;
      }

      const uri = await captureCard();
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(uri, {
          mimeType: 'image/png',
          dialogTitle: 'Compartilhar QR code do perfil',
        });
      }
    } catch {
      // usuário cancelou o share sheet
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <DashboardScrollView>
      <View className="gap-4 p-4">
        {/* Header */}
        <View className="flex-row items-center gap-3">
          <Pressable
            onPress={() => router.back()}
            className="h-10 w-10 items-center justify-center rounded-full bg-card"
            accessibilityLabel="Voltar"
          >
            <Ionicons className="text-neutral-400" name="arrow-back" size={20} />
          </Pressable>
          <View className="flex-1">
            <ThemedText className="text-xl font-bold">QR code do perfil</ThemedText>
            <ThemedText className="text-sm text-foreground/60">
              Imprima e cole no balcão, no carro ou no cartão de visita
            </ThemedText>
          </View>
        </View>

        {/* Cartão capturável: QR com a marca */}
        <View className="items-center">
          <View
            ref={cardRef}
            collapsable={false}
            className="items-center gap-3 rounded-2xl p-6"
            style={{ backgroundColor: '#FFFFFF' }}
          >
            <QRCode
              getRef={(ref) => {
                qrRef.current = ref;
              }}
              value={qrValue}
              size={240}
              color={brandColor}
              backgroundColor="#FFFFFF"
              logo={profile.logoUrl ? { uri: profile.logoUrl } : undefined}
              logoSize={52}
              logoBackgroundColor="#FFFFFF"
              logoBorderRadius={26}
              logoMargin={4}
              quietZone={8}
            />
            <ThemedText className="text-sm font-semibold" style={{ color: '#111' }}>
              {displayUrl}
            </ThemedText>
          </View>
        </View>

        <Card>
          <CardContent className="gap-3 py-4">
            <Button
              title={isSaving ? 'Salvando...' : 'Salvar na galeria'}
              disabled={isSaving}
              onPress={() => void handleSaveToGallery()}
            />
            <Button
              variant="outline"
              title={isSharing ? 'Abrindo...' : 'Compartilhar'}
              disabled={isSharing}
              onPress={() => void handleShare()}
            />
            <ThemedText className="text-center text-xs text-foreground/50">
              Quem apontar a câmera para o QR cai direto na sua vitrine.
            </ThemedText>
          </CardContent>
        </Card>
      </View>
    </DashboardScrollView>
  );
}
